import React, { useEffect } from 'react';

import { Button, ButtonGroup } from 'components/ui/button'
import { CheckboxInputElement } from 'components/ui/checkbox'
import { Description, Image, ListItemLabel, Title, Subtitle } from 'components/ui/list-item-label'
import { Modal } from 'components/ui/modal'
import { StatusGuard } from 'components/ui/status-guard'

import { sharedActionCreators, SharedActionCreators } from 'containers/shared'
import { connect } from 'containers/store'

import { actionCreators, GettyActionCreators } from 'domains/org/getty/actions'
import { stopSchema } from 'domains/org/getty/schemas/stop'
import { tourSchema } from 'domains/org/getty/schemas/tour'
import { GettyStore } from 'domains/org/getty/store'

import { Stop, Tour } from 'generated/mos/org/getty/audio'
import { ListToursRequest, GetStopRequest } from 'generated/mos/org/getty/audioaccess'
import { UpdateStopRequest } from 'generated/mos/org/getty/audiomanagement'
import { Media } from 'generated/mos/media'
import { Site } from 'generated/mos/structure';

import { bffEndpoint } from 'helpers/bff'
import { areArraysOfRefsEqual, pick } from 'helpers/core'
import { getResolvedValue } from 'helpers/linkables'
import { Status, statusSelector } from 'helpers/status'

import * as entity from 'entity';

type ConnectedProps =
  Pick<GettyStore['getty'], 'stops' | 'stopUpdate' | 'tours'| 'sites'>;

type ActionProps =
  Pick<GettyActionCreators, 'gettyUpdateStopRequest' | 'gettyListToursRequest' | 'gettyGetStopRequest' | 'gettyListSitesRequest'> &
  Pick<SharedActionCreators, 'toastNotification'>;

type DirectProps = {
  readonly onCancel: () => void;
  readonly onComplete: () => void;
  readonly stop: Stop.Entity;
};

type Props = ConnectedProps & ActionProps & DirectProps;

const StopUpdateToursView = ({
  gettyGetStopRequest,
  gettyListSitesRequest,
  gettyListToursRequest,
  gettyUpdateStopRequest,
  onCancel,
  onComplete,
  sites,
  stop,
  stopUpdate,
  toastNotification,
  tours,
}: Props) => {

  const toursData = statusSelector.data(tours);
  const stopData = statusSelector.data(stopUpdate);
  const stopTitle = stopData && stopData.stop.title ? getResolvedValue(stopSchema.title.read(stopData.stop.title), stopData.resolvedValues || []) : '';
  const tourRefList = entity.useSelectedRefs<Tour.Ref>([])
  const sitesData = statusSelector.data(sites)
  const stopTourRefs = stop && stop.tourRefs ? stop.tourRefs : []

  const resolveSiteName = (siteRef: Site.Ref | undefined) => {
    if (!siteRef) {
      return '-'
    }

    const site = sitesData && sitesData.sites && sitesData.sites.find((site) => Site.mustRef(site.ref).id === siteRef.id)
    return site ? site.name : '-'
  }

  const hasSubmitted = React.useRef(false);
  const hostName = bffEndpoint(window.location.hostname)

  // Get initial data on mount
  useEffect(() => {
    gettyGetStopRequest({ ...GetStopRequest.defaults, stopRef: stop.ref });
    gettyListToursRequest({ ...ListToursRequest.defaults });
    gettyListSitesRequest()
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // set selected tour refs
  useEffect(() => {
    if (stopData && stopUpdate.status === Status.Ready) {
      const tourRefs = stopData.stop.tourRefs
      tourRefList.setSelected(tourRefs ? tourRefs : [])
    }
  }, [stopUpdate.status]) // eslint-disable-line react-hooks/exhaustive-deps

 /* Enables to pass the comparison check when it has no values present */
  const currentRefs = stopTourRefs.map((ref => { return { id: ref.id, typename: ref.typename } }))
  const selectedRefs = tourRefList.selected.map((ref => { return { id: ref.id, typename: ref.typename } }))

  const modified = currentRefs && selectedRefs && areArraysOfRefsEqual(currentRefs, selectedRefs)

  const onSubmit = () => {
    gettyUpdateStopRequest({
      ...UpdateStopRequest.defaults,
      stopRef: stop.ref,
      updateMask: { ...UpdateStopRequest.defaults.updateMask, fields: ['tourRefs'] },
      tourRefs: tourRefList.selected,
    });
    hasSubmitted.current = true;
  };

  if (hasSubmitted.current && stopUpdate.status === Status.Ready) {
    toastNotification({ type: 'success', text: 'Changes saved' });
    // close on success.
    onComplete();
  }

  return (
    <Modal
      onClose={onCancel}
      header={<h3>Add “{stopTitle}” to tours</h3>}
      footer={(
        <ButtonGroup>
          <Button onClick={onCancel} appearance="secondary">Cancel</Button>
          <Button onClick={onSubmit} isDisabled={modified}>Save</Button>
        </ButtonGroup>
      )}
    >
      <StatusGuard status={tours} mode="form">
        <StatusGuard status={stopUpdate} mode="form">
          {toursData && toursData.map(tour => {
            const tourImage = tourSchema.imageRef.read(tour.imageRef) as Media.Ref
            const tourImageId = tourImage && tourImage.id
            const imagePath = tourImageId ? `${hostName}/images/${tourImageId}.jpg?s=crop&h=92&w=92` : ''

            return (
              <ListItemLabel key={Tour.mustRef(tour.ref).id}>
                <CheckboxInputElement
                  checked={tourRefList.includes(tour.ref)}
                  onClick={() => tourRefList.toggle(tour.ref)}
                />
                <Image src={imagePath} />
                <Description>
                  <Title>{tourSchema.name.read(tour.name)}</Title>
                  <Subtitle>{resolveSiteName(tour.siteRef)}</Subtitle>
                </Description>
              </ListItemLabel>
            )
          })}
        </StatusGuard>
      </StatusGuard>
    </Modal>
  );
}

export const StopUpdateTours = connect<ConnectedProps, ActionProps, DirectProps, GettyStore>(
    store => (pick(store.getty, 'stops', 'stopUpdate', 'tours', 'sites')),
    {
      ...pick(actionCreators, 'gettyUpdateStopRequest', 'gettyListToursRequest', 'gettyGetStopRequest', 'gettyListSitesRequest'),
      ...pick(sharedActionCreators, 'toastNotification'),
    }
)(StopUpdateToursView);
